import { yupResolver } from '@hookform/resolvers/yup';
import AppBar from '@mui/material/AppBar';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import { useForm, Controller } from 'react-hook-form';
import * as yup from 'yup';
import _ from '@lodash';
import { startLoading, stopLoading } from 'app/store/utilSlice';
import { selectCurrentFolder, uploadFileItem } from 'app/store/fileItemsSlice';
import { useSelector, useDispatch } from 'react-redux';
import { dispatchResultMessage } from '../utils';
import ControlledTextField from '../form/ControlledTextField';
import DefaultButton from '../layout/DefaultButton';
import MenuItem from '@mui/material/MenuItem';
import TextField from '@mui/material/TextField';
import { errorMessage } from 'app/shared-components/util/message';
import fileTypes from './fileTypes.json';

const INDEX_MODE_OPTIONS = [
  { value: '', label: 'None' },
  { value: 'TEXT', label: 'Text Search' },
  { value: 'SEMANTIC', label: 'Semantic Search' },
  { value: 'HYBRID', label: 'Hybrid (Text + Semantic)' },
];



/**
 * Form Validation Schema
 * 
 */
const schema = yup.object().shape({
  name: yup.string().required('You must enter a file name'),
  description: yup.string(),
  indexMode: yup.string(),
});

const MB= 1048576;
const maxFileSize= 100 * MB;
const maxFileSizeText= "100 MB";

function validateFile(file) 
{
	const out= {};
  const fileType= file?.type;
  const found = fileTypes.find((element) => element.type == fileType);
  out.validType= Boolean(found); 
  out.size= file?.size ? file.size : (maxFileSize + 1);
  out.type= fileType;
  out.maxSize= maxFileSize;
  out.maxSizeText= maxFileSizeText;
  out.validSize= out.size < out.maxSize;
  out.isValid= out.validType && out.validSize;
  out.message= !out.validType ? "File type is not allowed: " + fileType :
               !out.validSize ? "Maximum file size is " + out.maxSizeText :
               "";
  return out;
}

  
function UploadFileDialog(props) {
  const { file, onCloseDialog } = props;
  const dispatch = useDispatch(); 
  const open= Boolean(file);
  const folderId= useSelector(selectCurrentFolder);

  const defaultValues = {
	  name: '',
	  description: '',
	  indexMode: '',
  };
	
  const values = {
	  name: open ? file.name : '',
	  description: '',
	  indexMode: '',
  };
  
  const { handleSubmit, formState, control, reset } = useForm({
    mode: 'onChange',
    defaultValues: defaultValues,
    values: values,
    resolver: yupResolver(schema),
  });

  const { isValid, dirtyFields, errors } = formState;

  function handleCloseDialog() {
    onCloseDialog();
  }

  function handleDiscard() {
    onCloseDialog();
  }

  
  function onSubmit(data){
	  if (!file) return;
	  
	  const validation= validateFile(file);
	  if (!validation.isValid){
	     errorMessage(dispatch, validation.message);
	     return;
	  }
	  
	  dispatch(startLoading());
    onCloseDialog();
	  
	  dispatch(uploadFileItem({file: file, 
		                       folderId: folderId,
		                       name: data.name, 
		                       indexMode: data.indexMode || null,
		                       description: data.description})).then((resp) => {
		    dispatchResultMessage(dispatch, resp, "Upload Successful!")
    });
  }


  return (
      
      <Dialog
        open={open}
        onClose={handleCloseDialog}
        aria-labelledby="form-dialog-title"
        scroll="body"
      >
        <AppBar position="static" color="secondary" elevation={0}>
          <Toolbar className="flex w-full">
            <Typography variant="subtitle1" color="inherit">
              Upload File
            </Typography>
          </Toolbar>
        </AppBar>

        <form noValidate onSubmit={handleSubmit(onSubmit)} className="flex flex-col">
          <DialogContent classes={{ root: 'p-16 pb-0 sm:p-32 sm:pb-0' }}>
            <ControlledTextField name="name" label="File name"
                             control={control} errors={errors}
             				 autoFocus required fullWidth />
        
            <ControlledTextField name="description" label="File description"
                             control={control} errors={errors}
             				 required fullWidth />

            <Controller
              name="indexMode"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  select
                  label="Search Index Mode"
                  fullWidth
                  className="mt-16"
                  variant="outlined"
                >
                  {INDEX_MODE_OPTIONS.map((opt) => (
                    <MenuItem key={opt.value} value={opt.value}>{opt.label}</MenuItem>
                  ))}
                </TextField>
              )}
            />
          </DialogContent>

          <DialogActions className="flex flex-col sm:flex-row sm:items-center justify-between py-16 sm:py-24 px-24">

            <div className="flex items-center space-x-8 mt-16 sm:mt-0">
              <DefaultButton text="Cancel" onClick={handleDiscard} />
              <DefaultButton text="Upload"
	              disabled={_.isEmpty(dirtyFields) || !isValid}
	              submit />
            </div>
          </DialogActions>
        </form>
      </Dialog>
  );
}

export default UploadFileDialog;
